import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { useEffect } from "react";
import { SeoHead } from "@/components/common/SeoHead";
import { WhatsAppCTA } from "@/components/common/WhatsAppCTA";
import { AnimatedSection } from "@/components/common/AnimatedSection";
import { Navbar } from "@/components/layout/Navbar";
import { Footer } from "@/components/layout/Footer";
import { Button } from "@/components/ui/button";
import { Check, Users, Plane, Calendar, DollarSign, MapPin, Globe } from "lucide-react";

const BREADCRUMB = JSON.stringify({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: [
    { "@type": "ListItem", position: 1, name: "Home", item: "https://tripsyfy.com" },
    { "@type": "ListItem", position: 2, name: "Group Travel Planning", item: "https://tripsyfy.com/group-travel-planning" },
  ],
});

const FAQ_SCHEMA = JSON.stringify({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: [
    { "@type": "Question", name: "What is the minimum group size Tripsyfy can help with?", acceptedAnswer: { "@type": "Answer", text: "We handle groups from as small as 5 travelers up to 50 or more. Even smaller family groups of 4–6 benefit from our coordination, especially for international flights, multi-city routing, and private transfers." } },
    { "@type": "Question", name: "Can Tripsyfy coordinate group flights from different US cities?", acceptedAnswer: { "@type": "Answer", text: "Yes. One of our specialties is coordinating group travel where travelers depart from different US cities. We ensure everyone arrives at the destination within a coordinated window and that connections are properly timed." } },
    { "@type": "Question", name: "What kinds of groups does Tripsyfy handle?", acceptedAnswer: { "@type": "Answer", text: "We handle family reunions, destination weddings, church and faith community travel, corporate offsites, school and student travel, community organization trips, cultural and heritage tours, and friend groups traveling internationally." } },
    { "@type": "Question", name: "Does Tripsyfy offer group discounts on flights or tours?", acceptedAnswer: { "@type": "Answer", text: "Group pricing varies by airline, destination, and season. In many cases, airlines offer group fares for 10 or more travelers that are not available through online booking engines. We negotiate and find the best available pricing for your group." } },
    { "@type": "Question", name: "How far in advance should a group book their travel?", acceptedAnswer: { "@type": "Answer", text: "For groups of 10 or more, we recommend starting the process 4–6 months in advance, especially for peak season travel or complex multi-country trips. For smaller groups or off-peak travel, 2–3 months is usually sufficient." } },
    { "@type": "Question", name: "How do I start planning group travel with Tripsyfy?", acceptedAnswer: { "@type": "Answer", text: "Call us at +1 972-666-1122, message us on WhatsApp, or submit a group travel quote request. Share your group size, destination, travel dates, and the nature of your trip — we'll take it from there." } },
  ],
});

const groupTypes = [
  { icon: Users, title: "Family reunions", desc: "Multi-generational family groups, often spread across multiple US cities." },
  { icon: Globe, title: "Destination weddings", desc: "Coordinating flights and arrangements for the full wedding party and guests." },
  { icon: Users, title: "Church and faith groups", desc: "Pilgrimages, mission trips, and community travel with coordinated logistics." },
  { icon: MapPin, title: "Corporate and team travel", desc: "Company offsites, client trips, and team retreats to international destinations." },
  { icon: Plane, title: "Heritage and cultural tours", desc: "Groups traveling to ancestral homelands or culturally significant destinations." },
  { icon: Calendar, title: "Special occasion groups", desc: "Birthday celebrations, milestone trips, and friendship group travel." },
];

const whatWeHandle = [
  "Group flight search and negotiation with airlines",
  "Coordination of travelers departing from multiple US cities",
  "Ensuring synchronized arrivals within a tight time window",
  "Group hotel block reservations and room coordination",
  "Private group transfers (buses, vans, private vehicles)",
  "Group tour and activity bookings at destination",
  "Meal preference coordination and dietary requirement tracking",
  "Single point of contact for the entire group",
  "Itinerary documents for each traveler",
  "24/7 support during the trip",
];

const steps = [
  { num: "01", title: "Tell us about your group", desc: "Group size, destination, travel dates, and the nature of the trip. Multiple departure cities? No problem." },
  { num: "02", title: "We research and quote", desc: "We search group fares, coordinate routing options from multiple cities, and build a full group travel plan." },
  { num: "03", title: "You review and confirm", desc: "We present the plan and costs. You collect confirmations from your group and we lock in the bookings." },
  { num: "04", title: "We manage the details", desc: "From seat assignments to hotel blocks to on-the-ground transfers — we handle every moving part." },
];

const faqs = [
  { q: "What is the minimum group size Tripsyfy can help with?", a: "We handle groups from 5 travelers up to 50 or more. Even smaller family groups of 4–6 benefit from coordination, especially for international flights and multi-city routing." },
  { q: "Can Tripsyfy coordinate group flights from different US cities?", a: "Yes. We specialize in coordinating group travel where travelers depart from different US cities — ensuring everyone arrives within a coordinated window." },
  { q: "What kinds of groups does Tripsyfy handle?", a: "Family reunions, destination weddings, church groups, corporate offsites, student travel, community trips, cultural heritage tours, and international friend groups." },
  { q: "Does Tripsyfy offer group discounts on flights or tours?", a: "Group pricing varies by airline, destination, and season. Airlines often offer group fares for 10+ travelers that aren't available online. We negotiate and find the best pricing for your group." },
  { q: "How far in advance should a group book their travel?", a: "For groups of 10+, we recommend 4–6 months in advance, especially for peak season or complex trips. Smaller groups or off-peak travel can start 2–3 months out." },
  { q: "How do I start planning group travel with Tripsyfy?", a: "Call +1 972-666-1122, message us on WhatsApp, or submit a group travel quote. Share your group size, destination, dates, and trip type — we'll take it from there." },
];

export default function GroupTravelPlanningPage() {
  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <div id="main-content" className="min-h-screen bg-white">
      <Helmet>
        <script type="application/ld+json">{BREADCRUMB}</script>
        <script type="application/ld+json">{FAQ_SCHEMA}</script>
      </Helmet>
      <SeoHead
        title="Group Travel Planning"
        description="Tripsyfy helps families, friends, communities, and groups of all sizes coordinate flights, tours, and international travel — with a single point of contact for everything."
        canonical="https://tripsyfy.com/group-travel-planning"
      />
      <Navbar />

      <section className="relative pt-28 pb-14 bg-primary">
        <div className="container-wide px-4 sm:px-6 lg:px-8">
          <p className="text-sky-300 text-xs font-semibold tracking-widest uppercase mb-3">Group Travel Specialist</p>
          <h1 className="heading-xl text-white mb-3">Group Travel Planning</h1>
          <p className="text-base text-white/70 max-w-xl leading-relaxed mb-8">
            Tripsyfy handles group flights, tours, and full itinerary coordination for families, communities, corporate teams, and organized groups of all sizes — so you can focus on the journey, not the logistics.
          </p>
          <div className="flex flex-col sm:flex-row gap-3">
            <Link to="/contact"><Button className="bg-accent text-white hover:bg-accent/90">Request a Group Quote</Button></Link>
            <WhatsAppCTA variant="button" />
          </div>
        </div>
      </section>

      <section className="section-padding bg-surface-subtle">
        <div className="container-wide px-4 sm:px-6 lg:px-8">
          <AnimatedSection>
            <div className="max-w-2xl mb-10">
              <p className="overline mb-3">Who We Serve</p>
              <h2 className="heading-lg text-primary">What kinds of groups do we help?</h2>
            </div>
          </AnimatedSection>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {groupTypes.map(({ icon: Icon, title, desc }, i) => (
              <AnimatedSection key={title} delay={i * 0.06}>
                <div className="bg-white rounded-xl shadow-card p-5 flex gap-4">
                  <div className="h-10 w-10 rounded-xl bg-accent/10 flex items-center justify-center shrink-0">
                    <Icon className="h-5 w-5 text-accent" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-primary mb-1">{title}</p>
                    <p className="text-xs text-muted-foreground leading-relaxed">{desc}</p>
                  </div>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      <section className="section-padding bg-white">
        <div className="container-wide px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
            <AnimatedSection>
              <p className="overline mb-3">What We Handle</p>
              <h2 className="heading-lg text-primary mb-6">One contact. Every detail managed.</h2>
              <ul className="space-y-3">
                {whatWeHandle.map((item, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <div className="h-5 w-5 rounded-full bg-accent/10 flex items-center justify-center shrink-0 mt-0.5">
                      <Check className="h-3 w-3 text-accent" />
                    </div>
                    <span className="text-sm text-foreground leading-relaxed">{item}</span>
                  </li>
                ))}
              </ul>
            </AnimatedSection>
            <AnimatedSection delay={0.1}>
              <p className="overline mb-3">How It Works</p>
              <h2 className="heading-lg text-primary mb-6">Four steps to a coordinated group trip</h2>
              <div className="space-y-5">
                {steps.map(({ num, title, desc }) => (
                  <div key={num} className="flex gap-4">
                    <div className="text-3xl font-bold text-primary/10 leading-none w-10 shrink-0">{num}</div>
                    <div>
                      <p className="text-sm font-semibold text-primary mb-1">{title}</p>
                      <p className="text-sm text-muted-foreground leading-relaxed">{desc}</p>
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-8">
                <WhatsAppCTA variant="banner" />
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>

      <section className="section-padding bg-surface-subtle">
        <div className="container-wide px-4 sm:px-6 lg:px-8 max-w-3xl mx-auto">
          <AnimatedSection>
            <p className="overline mb-3 text-center">Common Questions</p>
            <h2 className="heading-lg text-primary text-center mb-10">Group Travel Planning</h2>
          </AnimatedSection>
          <div className="space-y-3">
            {faqs.map(({ q, a }, i) => (
              <AnimatedSection key={i} delay={i * 0.04}>
                <details className="bg-white rounded-xl shadow-card overflow-hidden">
                  <summary className="px-6 py-4 cursor-pointer font-semibold text-primary text-sm list-none flex items-center justify-between gap-4 hover:text-accent transition-colors">
                    {q}<span className="text-accent text-lg leading-none shrink-0">+</span>
                  </summary>
                  <div className="px-6 pb-5">
                    <p className="text-sm text-muted-foreground leading-relaxed">{a}</p>
                  </div>
                </details>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      <section className="section-padding bg-primary">
        <div className="container-wide px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 items-center">
            <div className="lg:col-span-2">
              <AnimatedSection>
                <h2 className="heading-lg text-white mb-3">Ready to plan your group trip?</h2>
                <p className="text-white/70 text-base leading-relaxed">Share your group size, destination, and dates — we'll handle the rest.</p>
              </AnimatedSection>
            </div>
            <div className="flex flex-col gap-3 lg:items-end">
              <Link to="/contact"><Button className="bg-accent text-white hover:bg-accent/90 w-full lg:w-auto">Request a Group Quote</Button></Link>
              <WhatsAppCTA variant="button" />
            </div>
          </div>
          <div className="mt-8 pt-8 border-t border-white/10 flex flex-wrap gap-6 text-sm text-white/60">
            <Link to="/custom-tour-packages" className="hover:text-white transition-colors">→ Custom Tour Packages</Link>
            <Link to="/international-flight-booking" className="hover:text-white transition-colors">→ International Flights</Link>
            <Link to="/flights-to-africa-from-usa" className="hover:text-white transition-colors">→ Flights to Africa from USA</Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
